import { Injectable, Inject, OnDestroy } from '@angular/core';
import {
  State,
  Action,
  INITIAL_STATE,
  ReducerObservable,
  ActionsSubject,
  ScannedActionsSubject,
} from '@ngrx/store';
import { Observable } from 'rxjs/Observable';
import { ReplaySubject } from 'rxjs/ReplaySubject';
import { Observer } from 'rxjs/Observer';
import { Subscription } from 'rxjs/Subscription';
import { map } from 'rxjs/operator/map';
import { merge } from 'rxjs/operator/merge';
import { observeOn } from 'rxjs/operator/observeOn';
import { scan } from 'rxjs/operator/scan';
import { skip } from 'rxjs/operator/skip';
import { withLatestFrom } from 'rxjs/operator/withLatestFrom';
import { queue } from 'rxjs/scheduler/queue';

import { DevtoolsExtension } from './extension';
import { liftAction, unliftAction, unliftState, applyOperators } from './utils';
import { liftReducerWith, liftInitialState, LiftedState } from './reducer';
import * as Actions from './actions';
import { StoreDevtoolsConfig, STORE_DEVTOOLS_CONFIG } from './config';
export class DevtoolsDispatcher extends ActionsSubject {static decorators: DecoratorInvocation[] = [
{ type: Injectable },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
];
}

function DevtoolsDispatcher_tsickle_Closure_declarations() {
/** @type {?} */
DevtoolsDispatcher.decorators;
/**
 * @nocollapse
 * @type {?}
 */
DevtoolsDispatcher.ctorParameters;
}

export class StoreDevtools implements Observer<any> {
private stateSubscription: Subscription;
public dispatcher: ActionsSubject;
public liftedState: Observable<LiftedState>;
public state: Observable<any>;
/**
 * @param {?} dispatcher
 * @param {?} actions$
 * @param {?} reducers$
 * @param {?} extension
 * @param {?} scannedActions
 * @param {?} initialState
 * @param {?} config
 */
constructor(
    dispatcher: DevtoolsDispatcher,
    actions$: ActionsSubject,
    reducers$: ReducerObservable,
    extension: DevtoolsExtension,
    scannedActions: ScannedActionsSubject,
     initialState: any,
     config: StoreDevtoolsConfig
  ) {
    const liftedInitialState = liftInitialState(initialState, config.monitor);
    const liftReducer = liftReducerWith(
      initialState,
      liftedInitialState,
      config.monitor,
      config.maxAge ? { maxAge: config.maxAge } : {}
    );

    const liftedAction$ = applyOperators(actions$.asObservable(), [
      [skip, 1],
      [merge, extension.actions$],
      [map, liftAction],
      [merge, dispatcher, extension.liftedActions$],
      [observeOn, queue],
    ]);

    const liftedReducer$ = map.call(reducers$, liftReducer);

    const liftedStateSubject = new ReplaySubject<LiftedState>(1);
    const liftedStateSubscription = applyOperators(liftedAction$, [
      [withLatestFrom, liftedReducer$],
      [
        scan,
        ({ state: liftedState }: any, [action, reducer]: any) => {
          const state = reducer(liftedState, action);

          extension.notify(action, state);

          return { state, action };
        },
        { state: liftedInitialState, action: null },
      ],
    ]).subscribe(({ state, action }) => {
      liftedStateSubject.next(state);

      if (action.type === Actions.PERFORM_ACTION) {
        const unlifedAction = (action as Actions.PerformAction).action;

        scannedActions.next(unlifedAction);
      }
    });

    const liftedState$ = liftedStateSubject.asObservable() as Observable<
      LiftedState
    >;
    const state$ = map.call(liftedState$, unliftState);

    this.stateSubscription = liftedStateSubscription;
    this.dispatcher = dispatcher;
    this.liftedState = liftedState$;
    this.state = state$;
  }
/**
 * @param {?} action
 * @return {?}
 */
dispatch(action: Action) {
    this.dispatcher.next(action);
  }
/**
 * @param {?} action
 * @return {?}
 */
next(action: any) {
    this.dispatcher.next(action);
  }
/**
 * @param {?} error
 * @return {?}
 */
error(error: any) {}
/**
 * @return {?}
 */
complete() {}
/**
 * @param {?} action
 * @return {?}
 */
performAction(action: any) {
    this.dispatch(new Actions.PerformAction(action));
  }
/**
 * @return {?}
 */
reset() {
    this.dispatch(new Actions.Reset());
  }
/**
 * @return {?}
 */
rollback() {
    this.dispatch(new Actions.Rollback());
  }
/**
 * @return {?}
 */
commit() {
    this.dispatch(new Actions.Commit());
  }
/**
 * @return {?}
 */
sweep() {
    this.dispatch(new Actions.Sweep());
  }
/**
 * @param {?} id
 * @return {?}
 */
toggleAction(id: number) {
    this.dispatch(new Actions.ToggleAction(id));
  }
/**
 * @param {?} index
 * @return {?}
 */
jumpToState(index: number) {
    this.dispatch(new Actions.JumpToState(index));
  }
/**
 * @param {?} nextLiftedState
 * @return {?}
 */
importState(nextLiftedState: any) {
    this.dispatch(new Actions.ImportState(nextLiftedState));
  }
static decorators: DecoratorInvocation[] = [
{ type: Injectable },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: DevtoolsDispatcher, },
{type: ActionsSubject, },
{type: ReducerObservable, },
{type: DevtoolsExtension, },
{type: ScannedActionsSubject, },
{type: undefined, decorators: [{ type: Inject, args: [INITIAL_STATE, ] }, ]},
{type: StoreDevtoolsConfig, decorators: [{ type: Inject, args: [STORE_DEVTOOLS_CONFIG, ] }, ]},
];
}

function StoreDevtools_tsickle_Closure_declarations() {
/** @type {?} */
StoreDevtools.decorators;
/**
 * @nocollapse
 * @type {?}
 */
StoreDevtools.ctorParameters;
/** @type {?} */
StoreDevtools.prototype.stateSubscription;
/** @type {?} */
StoreDevtools.prototype.dispatcher;
/** @type {?} */
StoreDevtools.prototype.liftedState;
/** @type {?} */
StoreDevtools.prototype.state;
}


interface DecoratorInvocation {
  type: Function;
  args?: any[];
}



// WEBPACK FOOTER //
// ../../../modules/store-devtools/src/devtools.ts